import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiPlus, FiChevronDown } from "react-icons/fi";
import JobForm from "./JobForm";
import StatCard from "./StatCard";
import ReviewForm from "./ReviewForm";
import { Stars } from "./Stars";
import { openChat } from "./chatActions";
import { apiRequest, errorMessage } from "../../api";
import { budgetText, cardClass, splitList, timeAgo, STATUS_LABELS, STATUS_STYLES } from "./jobUtils";

function ProposalsPanel({ job, currency, onChanged, onChat }) {
  const [proposals, setProposals] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest(`/core/cowork/jobs/${job.id}/proposals/`, { auth: true });
    if (ok) setProposals(data);
    else setError(errorMessage(data));
  }, [job.id]);

  useEffect(() => { load(); }, [load]);

  const decide = async (proposal, verb) => {
    if (verb === "accept" && !window.confirm(`Hire ${proposal.seller_name} for this job? All other pending proposals will be declined.`)) return;
    const { ok, data } = await apiRequest(`/core/cowork/proposals/${proposal.id}/${verb}/`, { method: "POST", auth: true });
    if (!ok) return setError(errorMessage(data));
    await load();
    onChanged();
  };

  if (error) return <p className="text-sm text-red-400 mt-4">{error}</p>;
  if (!proposals) return <p className="text-sm text-gray-500 mt-4">Loading proposals...</p>;
  if (proposals.length === 0) return <p className="text-sm text-gray-500 mt-4">No proposals yet. Sellers will appear here when they apply.</p>;

  return (
    <div className="mt-4 space-y-3">
      {proposals.map((p) => (
        <div key={p.id} className="rounded-lg bg-white/5 border border-navy-border p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold">{p.seller_name} <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[p.status]}`}>{STATUS_LABELS[p.status]}</span></p>
              {p.seller_headline && <p className="text-sm text-gray-400">{p.seller_headline}</p>}
              {splitList(p.seller_skills).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {splitList(p.seller_skills).slice(0, 6).map((s) => <span key={s} className="px-2 py-0.5 rounded bg-white/5 border border-navy-border text-xs text-gray-300">{s}</span>)}
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-accent-light">{currency} {p.bid_amount}{job.budget_type === "hourly" ? "/hr" : ""}</p>
              <p className="text-xs text-gray-400">{p.delivery_days} day{p.delivery_days === 1 ? "" : "s"} · {timeAgo(p.created_at)}</p>
            </div>
          </div>
          <p className="text-sm text-gray-300 mt-3 whitespace-pre-line">{p.cover_letter}</p>
          {p.seller_contact && (
            <p className="text-xs text-gray-400 mt-3">
              Contact: <a href={`mailto:${p.seller_contact.email}`} className="text-accent-light">{p.seller_contact.email}</a>
              {p.seller_contact.phone && <> · {p.seller_contact.phone}</>}
            </p>
          )}
          <button onClick={() => onChat(p.seller_id)} className="mt-3 mr-2 px-4 py-1.5 rounded-full border border-navy-border text-sm text-gray-200 hover:border-accent-light">
            Message {p.seller_name}
          </button>
          {p.status === "pending" && job.status === "open" && (
            <div className="inline-flex gap-2 mt-3">
              <button onClick={() => decide(p, "accept")} className="px-4 py-1.5 rounded-full bg-brand-gradient text-sm font-semibold">Accept &amp; Hire</button>
              <button onClick={() => decide(p, "reject")} className="px-4 py-1.5 rounded-full border border-navy-border text-sm text-gray-300 hover:text-white">Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function BuyerDashboard({ settings, startWithForm }) {
  const currency = settings?.currency_label || "USD";
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [showForm, setShowForm] = useState(!!startWithForm);
  const [editing, setEditing] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const chat = async (userId) => {
    setError("");
    const message = await openChat(userId, navigate);
    if (message) setError(message);
  };

  const load = useCallback(async () => {
    const [s, j] = await Promise.all([
      apiRequest("/core/cowork/dashboard/", { auth: true }),
      apiRequest("/core/cowork/jobs/mine/", { auth: true }),
    ]);
    if (s.ok) setStats(s.data);
    setJobs(j.ok ? j.data : []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveJob = async (payload) => {
    const { ok, data } = editing
      ? await apiRequest(`/core/cowork/jobs/${editing.id}/`, { method: "PATCH", auth: true, body: payload })
      : await apiRequest("/core/cowork/jobs/", { method: "POST", auth: true, body: payload });
    if (!ok) return errorMessage(data);
    setShowForm(false);
    setEditing(null);
    load();
    return "";
  };

  const jobAction = async (job, verb) => {
    setError("");
    if (verb === "delete" && !window.confirm("Delete this job and all its proposals?")) return;
    if (verb === "close" && !window.confirm("Close this job? Pending proposals will be declined.")) return;
    const { ok, data } = verb === "delete"
      ? await apiRequest(`/core/cowork/jobs/${job.id}/`, { method: "DELETE", auth: true })
      : await apiRequest(`/core/cowork/jobs/${job.id}/${verb}/`, { method: "POST", auth: true });
    if (!ok) return setError(errorMessage(data));
    load();
  };

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard label="Total jobs" value={stats?.jobs_total} />
        <StatCard label="Open" value={stats?.jobs_open} />
        <StatCard label="In progress" value={stats?.jobs_in_progress} />
        <StatCard label="Completed" value={stats?.jobs_completed} />
        <StatCard label="Proposals to review" value={stats?.proposals_pending} highlight />
      </div>

      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold">My Jobs</h2>
        {!showForm && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 px-5 py-2 rounded-full bg-brand-gradient font-semibold">
            <FiPlus /> Post a Job
          </button>
        )}
      </div>

      {showForm && (
        <div className={`${cardClass} p-6 mb-6`}>
          <h3 className="text-lg font-semibold mb-4">{editing ? "Edit job" : "Post a new job"}</h3>
          <JobForm
            key={editing ? editing.id : "new"}
            settings={settings}
            initial={editing}
            onSubmit={saveJob}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        </div>
      )}

      {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

      {jobs === null ? (
        <p className="text-gray-500">Loading...</p>
      ) : jobs.length === 0 ? (
        <div className={`${cardClass} text-center py-12`}>
          <p className="font-semibold">You haven't posted any jobs yet</p>
          <p className="text-gray-400 text-sm mt-1">Post your first job and start receiving proposals.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className={`${cardClass} p-5`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/co-work/jobs/${job.id}`} className="text-lg font-semibold hover:text-accent-light">{job.title}</Link>
                  <p className="text-xs text-gray-400 mt-1">
                    {job.category || "Uncategorised"} · {budgetText(job, currency)} · Posted {timeAgo(job.created_at)}
                    {!job.is_active && <span className="ml-2 text-amber-400">Hidden by admin</span>}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[job.status]}`}>{STATUS_LABELS[job.status]}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button
                  onClick={() => setOpenJob(openJob === job.id ? null : job.id)}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/5 border border-navy-border text-sm hover:border-accent-light"
                >
                  Proposals ({job.proposals_count}) <FiChevronDown className={openJob === job.id ? "rotate-180" : ""} />
                </button>
                {job.status === "open" && (
                  <>
                    <button onClick={() => { setEditing(job); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="px-4 py-1.5 rounded-full border border-navy-border text-sm text-gray-300 hover:text-white">Edit</button>
                    <button onClick={() => jobAction(job, "close")} className="px-4 py-1.5 rounded-full border border-navy-border text-sm text-gray-300 hover:text-white">Close</button>
                  </>
                )}
                {job.status === "in_progress" && (
                  <button onClick={() => jobAction(job, "complete")} className="px-4 py-1.5 rounded-full bg-brand-gradient text-sm font-semibold">Mark Completed</button>
                )}
                {job.status !== "in_progress" && (
                  <button onClick={() => jobAction(job, "delete")} className="px-4 py-1.5 rounded-full text-sm text-red-400 hover:bg-red-500/10">Delete</button>
                )}
              </div>

              {job.hired_seller && (
                <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-gray-300">Hired: <b className="text-white">{job.hired_seller.name}</b></span>
                  <button onClick={() => chat(job.hired_seller.user_id)} className="px-4 py-1.5 rounded-full border border-navy-border text-gray-200 hover:border-accent-light">Message</button>
                  {job.can_review && (
                    <button onClick={() => setReviewing(reviewing === job.id ? null : job.id)} className="px-4 py-1.5 rounded-full bg-brand-gradient font-semibold">
                      Rate {job.hired_seller.name}
                    </button>
                  )}
                  {job.my_review && (
                    <span className="text-gray-400 inline-flex items-center gap-1.5">You rated <Stars value={job.my_review.rating} size={12} /></span>
                  )}
                </div>
              )}
              {reviewing === job.id && job.hired_seller && (
                <ReviewForm jobId={job.id} who={job.hired_seller.name} onDone={() => { setReviewing(null); load(); }} onCancel={() => setReviewing(null)} />
              )}

              {openJob === job.id && <ProposalsPanel job={job} currency={currency} onChanged={load} onChat={chat} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
